import type { MouseEventHandler } from 'react';
import React, { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Box } from '@atlaskit/primitives/compiled';
import { updatePageRovoParams } from '@atlaskit/rovo-triggers';

import {
	CONVERSATION_ASSISTANT_CONTAINER_ID,
	CONVERSATION_ASSISTANT_CONTAINER_WIDTH,
} from '@atlassian/conversation-assistant/utils';
import {
	ConversationAssistantButton as ConversationAssistantButtonNav3,
	ConversationAssistantButtonNav4,
} from '@atlassian/conversation-assistant-ui-components';
import { ChatContextSubscriber } from '@atlassian/conversation-assistant-store';
import {
	CONVERSATION_ASSISTANT_BRAND_NAME,
	ChatOpenerSubscriber,
} from '@atlassian/conversation-assistant';

import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener';
import {
	ShortcutVisualizer,
	isMac,
	useShortcutStrings,
} from '@confluence/shortcuts/entry-points/ShortcutVisualizer';
import {
	AI_MATE_SHORTCUT,
	UNIVERSAL_AI_MATE_SHORTCUT_MAC,
	UNIVERSAL_AI_MATE_SHORTCUT_WIN,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import { useToggleConversationAssistant } from './hooks/useToggleConversationAssistant';

export const ConversationAssistantNavBarButton = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const rightSidebarContext = useRightSidebarContext();
	const { panelId } = rightSidebarContext.getCurrent() || {};
	const { cloudId } = useSessionData();
	const isNav4Enabled = useIsNav4Enabled();
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();
	const isOnEditRoute = useIsEditorPage();

	const isConversationAssistantSidebarActive = panelId === CONVERSATION_ASSISTANT_CONTAINER_ID;
	const { toggleConversationAssistant } = useToggleConversationAssistant();

	const handleOnClick = useCallback<MouseEventHandler>(
		(e) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'navigationItem',
					actionSubjectId: 'aiMate',
					source: 'AIMate',
					attributes: {
						opened: !isConversationAssistantSidebarActive,
						navigationLayer: 'global',
						navigationContainer: 'top',
						navVersion: isNav4Enabled ? '4' : '3',
					},
					tags: ['atlaskit', 'aiMate'],
				},
			}).fire();
			e.preventDefault();
			toggleConversationAssistant();
		},
		[
			createAnalyticsEvent,
			isConversationAssistantSidebarActive,
			isNav4Enabled,
			toggleConversationAssistant,
		],
	);

	const ConversationAssistantButton = isNav4Enabled
		? ConversationAssistantButtonNav4
		: ConversationAssistantButtonNav3;

	const universalAIMateShortcut = isMac()
		? UNIVERSAL_AI_MATE_SHORTCUT_MAC
		: UNIVERSAL_AI_MATE_SHORTCUT_WIN;

	const shortcutStrings = useShortcutStrings({ shortcut: universalAIMateShortcut });

	return (
		<Box as="span" paddingInlineStart="space.050" testId="app-navigation-ai-mate">
			<ConversationAssistantButton
				siteId={cloudId}
				productKey="confluence"
				onClick={handleOnClick}
				isActive={isConversationAssistantSidebarActive}
				tooltipContent={
					// No shortcut on edit pages
					isLiveEditMode || isOnEditRoute || fg('platform_tooltip_shortcuts_migration_1') ? (
						CONVERSATION_ASSISTANT_BRAND_NAME
					) : (
						<ShortcutVisualizer
							shortcut={universalAIMateShortcut}
							contentBefore={CONVERSATION_ASSISTANT_BRAND_NAME}
							isTooltip
						/>
					)
				}
				shortcut={
					// No shortcut on edit pages
					!isLiveEditMode && !isOnEditRoute && fg('platform_tooltip_shortcuts_migration_1')
						? shortcutStrings
						: undefined
				}
			/>
			<ChatContextSubscriber />
			<ChatOpenerSubscriber
				onTriggerOpeningChat={(conversationId, openChatMode) => {
					if (isConversationAssistantSidebarActive) {
						return;
					}

					if (
						fg('aifc_create_enabled') ||
						expValEquals('rovo_chat_refresh_v1_experiment', 'isRenderRefreshEnabled', true)
					) {
						// Make sure we select the correct conversation ID, if provided
						conversationId &&
							updatePageRovoParams({
								pathway: 'chat',
								conversationId,
							});

						toggleConversationAssistant(openChatMode);
					} else {
						rightSidebarContext.open(
							CONVERSATION_ASSISTANT_CONTAINER_ID,
							CONVERSATION_ASSISTANT_CONTAINER_WIDTH,
						);
					}
				}}
			/>
			<GeneralShortcutListener
				deprecatedAccelerator={!(isLiveEditMode || isOnEditRoute) ? AI_MATE_SHORTCUT : undefined}
				accelerator={universalAIMateShortcut}
				listener={() => toggleConversationAssistant('sidebar')}
				isAppNavigationShortcut
			/>
		</Box>
	);
};
